import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import { Button, ToggleSwitch, Breadcrumb, TextInput, Label } from "flowbite-react";
import { HiHome, HiPlus, HiFilter } from "react-icons/hi";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  useCreateGroupMutation,
  useGetGroupsQuery,
} from "@/api/groupApi/group";
import { useGetAgentGroupsQuery } from "@/api/agentApi/agent";
import GroupTable from "@/components/groups/GroupTable";
import GroupFormDialog from "@/components/groups/GroupFormDialog";
import { Group, DataGridPagination } from "@/sharedTypes";
import LoadingPage from "@/components/LoadingPage";
import { toast } from "@/lib/toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { agentRoles, isCompanyAdminUser } from "@/constants/agent";
import { useSelectedCompany } from "@/context/SelectedCompanyContext";
import SelectCompanyPrompt from "@/components/SelectCompanyPrompt";
import SuperAdminCompanyBar from "@/components/SuperAdminCompanyBar";

const GroupList = () => {
  const { t } = useTranslation(["group", "common"]);
  const user = useCurrentUser();
  // Company admin sees their own company's groups. Super admin sees the groups
  // of whichever company is picked in the header selector; everyone else sees
  // only the groups they belong to.
  const isSuperAdmin = user?.agent_role === agentRoles.super_admin;
  const isCompanyAdmin = isCompanyAdminUser(user);
  const isAdminView = isCompanyAdmin || isSuperAdmin;
  const { selectedCompany } = useSelectedCompany();
  const needsCompany = isSuperAdmin && !selectedCompany;
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showInactive, setShowInactive] = useState(false);
  const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
  const [groupNameFilter, setGroupNameFilter] = useState("");
  const [debouncedGroupName, setDebouncedGroupName] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedGroupName(groupNameFilter);
    }, 500);
    return () => clearTimeout(handler);
  }, [groupNameFilter]);

  useEffect(() => {
    setPage(1);
  }, [debouncedGroupName, showInactive]);

  const handleClearFilters = () => {
    setGroupNameFilter("");
    setShowInactive(false);
  };

  const handleToggleFilters = () => {
    if (showFilters) {
      handleClearFilters();
    }
    setShowFilters(!showFilters);
  };

  const queryParams = {
    pagination: { page, pageSize },
    filters: {
      is_active: !showInactive,
      ...(debouncedGroupName ? { group_name: debouncedGroupName } : {}),
      ...(isSuperAdmin && selectedCompany
        ? { company_id: selectedCompany.id }
        : {}),
    },
  };

  // Fetch all groups for admin views (company admin + super admin)
  const { data: allGroupsData, isLoading: isAllGroupsLoading } = useGetGroupsQuery(
    queryParams,
    { skip: !isAdminView || needsCompany }
  );

  // Fetch agent's groups if not admin
  const { data: agentGroupsData, isLoading: isAgentGroupsLoading } = useGetAgentGroupsQuery(
    { agentId: user?.agent_id?.toString() || "", params: queryParams },
    { skip: isAdminView || !user?.agent_id }
  );

  const groupsData = isAdminView ? allGroupsData : agentGroupsData;
  const isLoading = isAdminView ? isAllGroupsLoading : isAgentGroupsLoading;

  const [createGroup] = useCreateGroupMutation();

  const handleCreateSubmit = async (data: Group) => {
    try {
      await createGroup(data).unwrap();
      toast.success(t("group:groupCreated"));
      setIsCreationModalOpen(false);
    } catch (error) {
      toast.error(t("group:createFailed"));
    }
  };

  const paginationData = groupsData?.pagination;
  const totalItems = paginationData?.totalItems ?? groupsData?.items?.length ?? 0;
  const calculatedTotalPages =
    Math.ceil(totalItems / (paginationData?.pageSize || pageSize)) || 1;

  const pagination: DataGridPagination = {
    pageIndex: paginationData?.pageIndex || 1,
    pageSize: paginationData?.pageSize || 10,
    totalPages: paginationData?.totalPages ?? calculatedTotalPages,
    totalItems: totalItems,
  };

  return (
    <NavbarSidebarLayout>
      {isLoading ? <LoadingPage /> : <div className="h-screen overflow-y-auto pb-20">
        <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
          <div className="col-span-full">
            <Breadcrumb className="mb-4">
              <Breadcrumb.Item href="/">
                <div className="flex items-center gap-x-3">
                  <HiHome className="text-xl" />
                  <span className="dark:text-white">{t("common:home")}</span>
                </div>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{t("group:groups_title")}</Breadcrumb.Item>
            </Breadcrumb>

            <SuperAdminCompanyBar />

            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("group:groups_title")}
                </h1>
                <p className="text-gray-500">{t("group:manage_groups")}</p>
              </div>
              {isCompanyAdminUser(user) && (
                <Button
                  color="primary"
                  onClick={() => setIsCreationModalOpen(true)}
                >
                  <HiPlus className="mr-2 h-5 w-5" />
                  {t("group:createGroup")}
                </Button>
              )}
            </div>
            <div className="mt-6 flex justify-start">
              <Button color="light" onClick={handleToggleFilters}>
                <HiFilter className="mr-2 h-5 w-5" />
                {showFilters
                  ? t("common:hideFilters")
                  : t("common:showFilters")}
              </Button>
            </div>

            {showFilters && (
              <div className="mt-6 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
                <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
                  {t("common:filters")}
                </h3>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                  <div>
                    <Label htmlFor="group-search" className="mb-2 block">
                      {t("group:groupName")}
                    </Label>
                    <TextInput
                      id="group-search"
                      placeholder={t("group:searchGroup")}
                      className="mt-4"
                      value={groupNameFilter}
                      onChange={(e) => setGroupNameFilter(e.target.value)}
                    />
                  </div>

                  <div className="flex flex-col justify-end gap-4 pb-2">
                    <ToggleSwitch
                      checked={showInactive}
                      label={t("group:showInactive")}
                      onChange={setShowInactive}
                    />
                    <Button
                      color="light"
                      size="sm"
                      onClick={handleClearFilters}
                    >
                      {t("common:reset")}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
          {needsCompany ? (
            <SelectCompanyPrompt />
          ) : (
            <GroupTable
              data={groupsData?.items || []}
              pagination={pagination}
              onPageChange={setPage}
              showInactive={showInactive}
            />
          )}
        </div>

        <GroupFormDialog
          open={isCreationModalOpen}
          onClose={() => setIsCreationModalOpen(false)}
          onSubmit={handleCreateSubmit}
          mode="create"
        />
      </div>}
    </NavbarSidebarLayout>
  );
};

export default GroupList;
